import {
  getOutcomeResultForValueId,
  getOutcomeValueFromDefinitionAndGrading,
  type GradingOutcome,
  type OutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import { UNKNOWN_CONTACT_DISPLAY_NAME } from "@repo/app-types/helpers/utilities";
import { Size } from "@repo/ui/helpers/helpers";
import { OutcomeResultPill } from "@repo/ui/Pills/outcomeResultPill";
import { IconUser } from "@tabler/icons-react";
import { DateTime } from "luxon";
import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

export const RECENT_MATCHES_DISPLAY_LIMIT = 20;

/**
 * Optional labeled pill in the header cluster of a match card.
 * Renders as `{label} [pill]` — caller controls both the label and the
 * pill rendering so this component stays agnostic of taxonomy.
 */
export interface MatchListItemHeaderBadge {
  label: ReactNode;
  pill: ReactNode;
}

interface MatchListItemProps {
  contactName: string | null;
  recordedAt: string | null;
  teammateName: string;
  bodyText: string | null;
  outcome: GradingOutcome | null;
  outcomeDef: OutcomeDefinition | undefined;
  onClick: () => void;
  className?: string;
  /**
   * Optional extra metadata for the right-side header cluster. Outcome
   * always renders in that cluster when present; callers can add a
   * second labeled pill such as `CX score [4 · Good]`.
   */
  headerBadge?: MatchListItemHeaderBadge | null;
}

export const MatchListItem: FC<MatchListItemProps> = ({
  contactName,
  recordedAt,
  teammateName,
  bodyText,
  outcome,
  outcomeDef,
  onClick,
  className,
  headerBadge,
}) => {
  const dt = recordedAt ? DateTime.fromISO(recordedAt) : null;
  const formattedDate =
    dt && dt.isValid ? dt.toLocaleString(DateTime.DATE_MED) : "—";
  const outcomeResult = outcomeDef
    ? getOutcomeResultForValueId(
        outcomeDef,
        getOutcomeValueFromDefinitionAndGrading(outcomeDef, outcome),
      )
    : undefined;

  const showsHeaderCluster = Boolean(outcomeResult || headerBadge);

  return (
    <button
      type="button"
      className={twMerge(
        "w-full px-4 py-3 text-left transition-colors hover:bg-blue-50",
        className,
      )}
      onClick={onClick}
    >
      <div className="space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
            <span className="font-semibold text-foreground">
              {contactName ?? UNKNOWN_CONTACT_DISPLAY_NAME}
            </span>
            <span className="text-gray-400">&middot;</span>
            <span className="text-xs text-muted-foreground">
              {formattedDate}
            </span>
          </div>

          {showsHeaderCluster ? (
            <div className="flex shrink-0 flex-wrap items-center gap-3 sm:justify-end">
              {outcomeResult ? (
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">Outcome</span>
                  <OutcomeResultPill size={Size.SMALL} result={outcomeResult} />
                </span>
              ) : null}
              {headerBadge ? (
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">
                    {headerBadge.label}
                  </span>
                  {headerBadge.pill}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="-ml-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
          <IconUser className="size-4 shrink-0" />
          <span>{teammateName}</span>
        </div>

        {bodyText ? (
          <p className="line-clamp-2 text-sm text-muted-foreground">
            {bodyText}
          </p>
        ) : null}
      </div>
    </button>
  );
};
